import type { AuditRow } from './api/endpoints'
import { formatRupiah } from './money'
import { t } from './strings'

/** Who did it, in words the owner knows. */
export function actorLabel(type: string): string {
  return { owner: 'Anda (web)', device: 'HP kasir', device_user: 'HP kasir', support: 'Tim Alfacode', system: 'Sistem' }[type] ?? type
}

export type AuditCategory = 'sales' | 'catalog' | 'staff' | 'devices' | 'settings' | 'other'

export const AUDIT_CATEGORIES: { key: AuditCategory; label: string }[] = [
  { key: 'sales', label: 'Transaksi' },
  { key: 'catalog', label: 'Layanan & harga' },
  { key: 'staff', label: 'Staf & komisi' },
  { key: 'devices', label: 'HP kasir' },
  { key: 'settings', label: 'Pengaturan' },
]

/** Which filter a change belongs to, from its technical name. */
export function auditCategory(action: string): AuditCategory {
  const group = action.split('.')[0] ?? ''
  if (group === 'sale') return 'sales'
  if (group === 'catalog_item') return 'catalog'
  if (group === 'staff' || group === 'commission_rule') return 'staff'
  if (['device', 'device_user', 'activation_code', 'emergency_export'].includes(group)) return 'devices'
  if (group === 'outlet') return 'settings'
  return 'other'
}

/** One line of plain Indonesian for a change. An unknown kind still reads as a sentence, never as a code. */
export function describeAudit(r: AuditRow): string {
  const d = r.detail
  const changes = (d.changes ?? {}) as Record<string, unknown>
  const fields = Object.keys(changes)
    .map((f) => t.fields[f] ?? t.kasirFieldNames[f] ?? f)
    .join(', ')
  switch (r.action) {
    case 'sale.voided':
      return `Transaksi dibatalkan (${typeof d.amount === 'number' ? formatRupiah(d.amount) : ''}): ${String(d.reason ?? '')}`
    case 'catalog_item.created':
      return `Layanan baru: ${String(changes.name ?? '')}`
    case 'catalog_item.updated':
      return 'price' in changes && typeof changes.price === 'number' ? `Harga diubah ke ${formatRupiah(changes.price)}` : `Layanan diubah: ${fields}`
    case 'staff.created':
      return `Staf baru: ${String(changes.name ?? '')}`
    case 'staff.updated':
      return `Staf diubah: ${fields}`
    case 'commission_rule.created':
    case 'commission_rule.updated':
      return `Aturan komisi diubah: ${fields}`
    case 'device_user.created':
    case 'device_user.updated':
      return `Pengguna kasir diubah: ${fields}`
    case 'device.activated':
      return `HP ${String(d.letter ?? '')} diaktifkan`
    case 'device.replaced':
    case 'device.force_replaced':
      return 'HP kasir diganti'
    case 'device.released':
      return 'HP kasir dilepas'
    case 'emergency_export.imported':
      return `Ekspor darurat dipulihkan (${String(d.accepted ?? 0)} transaksi)`
    case 'outlet.updated':
      return 'Pengaturan outlet diubah'
    case 'outlet.settings_updated':
      return `Pengaturan kasir diubah: ${fields}`
    case 'outlet.profile_applied':
      return `Pengaturan awal ${d.business_type === 'food' ? 'Makanan & minuman' : 'Jasa'} diterapkan`
    case 'outlet.logo_updated':
      return 'Logo struk diganti'
    case 'outlet.logo_removed':
      return 'Logo struk dihapus'
    case 'activation_code.created':
      return 'Kode aktivasi dibuat'
    default:
      return `Perubahan lain (${r.action})`
  }
}
